import React, { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';

interface BreathingExerciseProps {
  onComplete: (score: number) => void;
}

export default function BreathingExercise({ onComplete }: BreathingExerciseProps) {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('לחצו על התחל');
  const [scale, setScale] = useState(1);
  const [timeLeft, setTimeLeft] = useState(60); 

  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
            setIsActive(false);
            onComplete(30);
            return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isActive, onComplete]);

  useEffect(() => {
    if (!isActive) return;

    // Initial state set
    setPhase('שאפו אוויר...');
    setScale(1.5);
  }, [isActive]);
  
  // Re-implementing the breathe loop cleanly
  useEffect(() => {
      if (!isActive) return;
      
      let phaseTimeout: ReturnType<typeof setTimeout>;
      
      const runCycle = () => {
          setPhase('שאפו אוויר...');
          setScale(1.5);
          
          phaseTimeout = setTimeout(() => {
              setPhase('החזיקו...');
              
              phaseTimeout = setTimeout(() => {
                  setPhase('נשפו החוצה...');
                  setScale(1);
                  
                  phaseTimeout = setTimeout(() => {
                      setPhase('הירגעו...');
                  }, 4000);
              }, 4000);
          }, 4000);
      };

      runCycle();
      const cycleInterval = setInterval(runCycle, 16000);
      
      return () => {
          clearInterval(cycleInterval);
          clearTimeout(phaseTimeout);
      };
  }, [isActive]);

  const handleStart = () => {
      setIsActive(true);
      setTimeLeft(60);
  };

  return (
    <div className="flex flex-col items-center justify-center h-[60vh] text-foreground">
        {!isActive ? (
            <div className="flex flex-col items-center">
                <div className="w-48 h-48 rounded-full border-4 border-teal-500/30 flex items-center justify-center mb-8">
                     <Zap size={64} className="text-teal-400" />
                </div>
                <h2 className="text-2xl font-bold mb-4">תרגיל נשימה</h2>
                <p className="text-muted-foreground mb-8 text-center max-w-xs">
                    קחו רגע לעצמכם. נשמו עמוק ונסו להירגע במשך דקה אחת.
                </p>
                <button 
                    onClick={handleStart}
                    className="bg-teal-500 hover:bg-teal-400 text-white px-12 py-4 rounded-full font-bold text-xl shadow-lg transition-transform hover:scale-105"
                >
                    התחל תרגול
                </button>
            </div>
        ) : (
            <>
                <div 
                    className="w-48 h-48 rounded-full bg-gradient-to-br from-teal-400 to-emerald-600 blur-xl absolute opacity-30 transition-all duration-[4000ms] ease-in-out"
                    style={{ transform: `scale(${scale * 1.5})` }}
                />
                <div 
                    className="w-48 h-48 rounded-full border-4 border-teal-500 flex items-center justify-center relative z-10 transition-all duration-[4000ms] ease-in-out shadow-[0_0_50px_rgba(45,212,191,0.3)]"
                    style={{ transform: `scale(${scale})` }}
                >
                    <span className="text-xl font-bold text-center">{phase}</span>
                </div>
                
                <p className="mt-12 text-muted-foreground">זמן שנותר: {timeLeft} שניות</p>
            </>
        )}
    </div>
  );
}
